/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team <mailto:radmanesh@gmail.com Arman Radmanesh> <mailto:salarirad@gmail.com Salar Rad>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { Timestamp, collection, doc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../firebase';



/**
 * Retrieves the document of the currently signed-in user.
 * @returns {Promise<Object>} The user document data.
 * @throws {Error} If no user is currently signed in or if the user document doesn't exist.
 */
export const getCurrentUserDocument = async () => {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No user currently signed in');
  }

  // Get the document for the current user
  const q = query(collection(db, "users"), where("uid", "==", user.uid));
  const docs = await getDocs(q);

  // TODO: I should create the user document if it doesn't exist
  if (docs.docs.length === 0) {
    console.log('no user document found for this user', user.uid);
    return null;
  }
  const userDoc = docs.docs[0];

  // If the user document doesn't exist, throw an error
  if (!userDoc.exists) {
    throw new Error(`No document found for user with UID: ${user.uid}`);
  }

  return userDoc;
};

/**
 * Retrieves the user document from the Firestore database based on the provided UID.
 * @param {string} uid - The UID of the user.
 * @returns {Promise<Object|null>} - A promise that resolves with the user document data if found, or null if not found.
 * @throws {Error} - Throws an error if the user document doesn't exist.
 */
export const getUserDocument = async (uid) => {
  const q = query(collection(db, "users"), where("uid", "==", uid));
  const docs = await getDocs(q);


  if (docs.docs.length === 0) {
    console.log('no user document found for this user', uid);
    return null;
  }

  const userDoc = docs.docs[0];

  // If the user document doesn't exist, throw an error
  if (!userDoc.exists) {
    console.log('no user document found for this user', uid)
    throw new Error(`No document found for user with UID: ${uid}`)
  }

  return userDoc;
};

/**
 * Generates a user document in firestore from a Firebase user object.
 * @param {Object} user - The Firebase user object.
 * @param {Object} additionalData - Additional data to be included in the user document.
 * @returns {Promise<Object>} - A promise that resolves to the user document reference.
 * @throws {Error} - If no user is found or if a user document already exists for the user.
 */
export const getOrGenerateUserDocumentFromFirebaseUser = async (user, additionalData) => {
  console.log("generateUserDocumentFromFirebaseUser : user is : %o" , user, additionalData);
  if (!user || !user.uid) {
    console.log('No user found: uid: %s',user?.uid);
    throw new Error('No user found');
  }
  const q = query(collection(db, "users"), where("uid", "==", user.uid));
  const docs = await getDocs(q);
  if (docs.docs.length > 0 && docs.docs[0].exists()) {
    console.log('user document already found for this user', user.uid);
    return docs.docs[0];
  }
  const userDocRef = await setDoc(doc(db, "users", user.uid), {
    uid: user.uid,
    email: user.email,
    name: user.displayName || '',
    photoURL: user.photoURL,
    createdAt: serverTimestamp(),
    ...additionalData,
  });
  if(userDocRef?.getDocs()?.docs?.length>0)
    return userDocRef.docs[0];
  else
    return null;
}


/**
 * Fetches FitBit users from the database.
 * @returns {Promise<Array<Object>>} An array of FitBit user data.
 */
export const fetchFitBitUsers = async () => {
  const q = query(collection(db, "participants"), where("fitbitData.access_token", ">", null));
  const docs = await getDocs(q);
  return docs.docs.map(doc => doc.data());
}

export const setFitbitAuthData = async (userId, fitbitData) => {
  if (!userId || !fitbitData?.access_token) {
    throw new Error('userId and fitbitData are required');
  }
  const res = await updateDoc(doc(db, "users", userId), {
    "fitbitData": {...fitbitData , timestamp: Timestamp.now()},
    "fitbitRequest.challenge": null,
    "fitbitRequest.state": null
  });
  return res;
}

// TODO: Add more functions here to interact with Firestore and Firebase Auth
export const checkUserExistsAndPhoneIsNotVerified = (user) => {
  //todo check the firebase too.
  //console.log(" checkUserExistsAndPhoneIsNotVerified , user is " + user);
  if (!!user || !user?.uid) {
    return false;
  }
  if (user?.uid) {
    if (user?.phoneNumberVerified)
      return true;
    else
      return false;
  }
}

/**
 * Returns whether the current user's phone number is verified or not.
 *
 * This function checks if the user's phone number is verified in three places:
 * - The provider data of the current user (which is where Firebase stores phone number verification info).
 * - The phoneVerified field of the user document in the users collection in Firestore.
 * - The invitees collection in Firestore (which is where we store the phone number verification status of newly registered users).
 *
 * @returns {boolean} - Whether the current user's phone number is verified or not.
 */
export const getCurrentUserPhoneVerified = () => {
  const user = auth.currentUser;
  if (!user || !user?.uid) {
    return false;
  }

  // Check if the phone number is verified in the provider data of the current user.
  const phoneVerifiedInProviderData = user.providerData.find(predicate =>
    predicate?.providerId === "phone" && predicate?.phoneNumber?.length > 0);
  if (phoneVerifiedInProviderData) {
    return true;
  }

  // Check if the phone number is verified in the user document in the users collection in Firestore.
  const docSnapshot = doc(collection(db, "users"), user.uid).get();
  if (docSnapshot.then(doc => doc?.data()?.phoneVerified === true)) {
    return true;
  }

  return false;
}
